import { MessageBroadcastingService } from '../message-broadcasting.service';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { BroadcastThreadViewModel } from '../../api/models/broadcast-thread-view-model';
import { MessageBroadcastThreadModel } from '../../api/models/message-broadcast-thread-model';
import { EventEmitter, Injectable } from '@angular/core';
import { MessagingGroupModel } from '../../api/models/messaging-group-model';

export interface FilterOptions {
  phoneNumber?: string;
  broadcastId?: string;
  searchText?: string;
}

@Injectable()
export class BroadcastPhoneNumberGroupDatasource implements DataSource<BroadcastThreadViewModel | undefined> {
  private readonly TAG = 'BroadcastPhoneNumberGroupDatasource:';
  private _length = 0;
  private _pageSize = 100;
  private _currentPage = 0;
  private _cachedData = Array.from<BroadcastThreadViewModel>({ length: 0 });
  private _fetchedPages = new Set<number>();
  private _dataStream = new BehaviorSubject<(BroadcastThreadViewModel | undefined)[]>(this._cachedData);
  private _subscription = new Subscription();
  private _newMessageSubscription = new Subscription();
  private _newNotificationsSubscription = new Subscription();

  selectedThreadChangeEvent = new EventEmitter<BroadcastThreadViewModel>();
  filterOptions: FilterOptions = {};

  constructor(public messagingService: MessageBroadcastingService) {}

  connect(collectionViewer: CollectionViewer): Observable<(BroadcastThreadViewModel | undefined)[]> {
    this._subscription = collectionViewer.viewChange.subscribe(async range => {
      console.log('range subscription', range);
      const startPage = this._getPageForIndex(range.start);
      const endPage = this._getPageForIndex(range.end - 1);
      for (let i = startPage; i <= endPage; i++) {
        await this._fetchPage(i);
      }
    });

    this._newMessageSubscription = this.messagingService.onNewMessageSubject.subscribe(async message => {
      await this._updateGroups(message);
    });

    return this._dataStream;
  }

  disconnect(): void {
    console.log(`${this.TAG} disconnect`);
    this._subscription?.unsubscribe();
    this._newMessageSubscription?.unsubscribe();
    this._newNotificationsSubscription?.unsubscribe();

    this._length = 0;
    this._fetchedPages = new Set<number>();

    this._cachedData = Array.from({ length: 0 });
    this._dataStream.next(this._cachedData);
  }

  public async resetData(): Promise<void> {
    console.log(`${this.TAG} resetData`);
    await this._refreshData(0, true);
  }

  public async refreshCurrentPages() {
    console.log(`${this.TAG} refreshCurrentPages`);
    await this._refreshData(this._currentPage, true);
    this._dataStream.next(this._cachedData);
  }

  onFilterDataChanged(filterOptions: FilterOptions): void {
    console.log(`${this.TAG} onFilterDataChanged`, filterOptions);

    if (this.filterOptions.phoneNumber !== filterOptions.phoneNumber || this.filterOptions.searchText !== filterOptions.searchText) {
      this.filterOptions = filterOptions;
      this._refreshData(0, true).then();
      return;
    }

    this.filterOptions = filterOptions;
    if (this.filterOptions.broadcastId === 'new') {
      return;
    }

    let selectedGroup = this._cachedData.find(x => x.id === (this.filterOptions.broadcastId || 'undefined'));

    if (!selectedGroup) {
      this._refreshData(this._currentPage, true).then();
    } else {
      this.selectGroup();
    }

  }

  private selectGroup() {
    console.log(`${this.TAG} selectGroup`, this.filterOptions.broadcastId);
    let selectedGroup = this._cachedData.find(x => x.id === (this.filterOptions.broadcastId || 'undefined'));

    if (!selectedGroup) {
      selectedGroup = this._cachedData[0];
    }

    this.selectedThreadChangeEvent.emit(selectedGroup);

    console.log(`${this.TAG} selectedGroup`, selectedGroup, this.filterOptions.broadcastId);
  }

  sortGroups() {
    this._cachedData.sort((a, b) => {
      const aTime = new Date(a.lastTime);
      const bTime = new Date(b.lastTime);

      return bTime.getTime() - aTime.getTime();
    });
  }

  private _getPageForIndex(index: number): number {
    this._currentPage = Math.floor(index / this._pageSize);
    return this._currentPage;
  }

  private async _refreshData(page: number, resetPages: boolean): Promise<void> {
    if (resetPages) {
      this._fetchedPages = new Set<number>();
      this._length = 0;
      this._cachedData = Array.from({ length: 0 });
    }
    await this._fetchPage(page);
    this.selectGroup();
  }

  private async _updateGroups(data: MessageBroadcastThreadModel) {
    if (!data) {
      return;
    }

    if (!this.filterOptions.phoneNumber) {
      return;
    }

    console.log(`${this.TAG} Update broadcast groups`);

    const existingGroup = this._cachedData.find(x => !!x && x.id === data.broadcastId);

    if (!existingGroup) {
      await this._refreshData(this._currentPage, true);
    } else {
      existingGroup.lastBroadcastKey = data.broadcastKey;
      existingGroup.lastTime = data.lastTime;
      existingGroup.lastMessage = data.message;
      existingGroup.total = data.total;
      existingGroup.received = data.received;
      existingGroup.error = data.error;

      this.sortGroups();
      this._dataStream.next(this._cachedData);
    }
  }

  private async _fetchPage(page: number) {
    console.log('_fetchPage ---- Datasource ' + page);
    if (this._fetchedPages.has(page)) {
      return;
    }
    this._fetchedPages.add(page);

    if (!this.filterOptions.phoneNumber) {
      this._cachedData = Array.from<BroadcastThreadViewModel>({ length: 0 });
      this._dataStream.next(this._cachedData);
      return;
    }

    const request = {
      phoneNumber: this.filterOptions.phoneNumber,
      searchText: this.filterOptions.searchText,
      limit: this._pageSize,
      skip: this._pageSize * page,
      forceUpdate: false,
    };

    console.log('_fetchPage ---- get broadcasts' + page, request);
    const groups = await this.messagingService.queryBroadcastThreads(request);
    console.log('_fetchPage ---- after get messaging threads total:' + groups.length, request);

    if (this._length === 0) {
      this._length = this.messagingService.totalGroups;
      this._cachedData = Array.from<BroadcastThreadViewModel>({ length: this._length });
    }

    this._length = this.messagingService.totalGroups;
    this._cachedData.splice(request.skip, groups.length, ...groups);

    this.sortGroups();
    this._dataStream.next(this._cachedData);
  }
}
